/* eslint-disable no-console */
import React, { useEffect, useCallback } from "react";
import { UxAction, UxPriorityEnum } from "@imaginelearning/mps-ux-component-library";
import { GoPeerService } from "@imaginelearning/mps-api-service";
import { useFeatureFlag } from "@imaginelearning/shared-components/useFeatureFlag";
import useScreenshot from "../../hooks/usescreenshot";

import useScript from "../../hooks/useScript";
import useSessionStorage from "../../hooks/useSessionStorage";
import useGradeRange from "../../hooks/usegraderange";

// As defined in https://github.com/GoPeerProject/gp-script-demo
export interface UserData {
  userId: string;
  firstName: string;
  lastName: string;
  name: string;
  districtId?: string | number;
  districtName?: string;
  schoolId?: string | number;
  courseId?: string;
  schoolName?: string;
  courseName?: string;
  lessonName?: string;
  activityName?: string;
  email?: string;
  subject?: string;
  grade?: string;
}

export interface Link {
  title: string;
  url: string;
  text: string;
}

declare global {
  interface Window {
    gP: any;
    STUDENT_ENV: any;
  }
}

interface GoPeerButtonProps {
  onClick?: Function;
  text: string;
  disabled: boolean;
}

const useGoPeer = (userData: UserData, links: Link[] = []) => {
  useScript("https://js.gopeer.org/launcher.js");
  const { takeScreenshot } = useScreenshot();

  const IdentifyURL = window.STUDENT_ENV.REACT_APP_ONDEMAND_IDENTIFY_URL;

  const [token, setToken] = useSessionStorage<string | null>("gopeer-token", null);

  const { getFlagValue } = useFeatureFlag();
  const isOnDemandTutoringEnabled = getFlagValue("enableOnDemandTutoring") === "on";
  const { gradeButtonStyle } = useGradeRange();

  const getEnv = () => {
    // Use the Identify URL to get the correct environment
    if (IdentifyURL.includes("dev")) {
      return "dev";
    }
    return "prod";
  };

  const addLink = (title: string, url: string, text: string) => {
    if (!url || !title || !text) {
      return;
    }
    links.push({
      title,
      url,
      text
    });
  };

  const fetchToken = useCallback(async () => {
    if (!isOnDemandTutoringEnabled) {
      setToken(null);
      return;
    }
    GoPeerService.getGoPeerToken().then((response: any) => {
      if (typeof response.response === "string") {
        setToken(response.response);
      }
    });
  }, [isOnDemandTutoringEnabled, setToken]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  function GoPeerButton({ onClick, text, disabled = false }: GoPeerButtonProps) {
    if (!isOnDemandTutoringEnabled) {
      return null;
    }

    const launchGoPeer = async () => {
      const image = await takeScreenshot();
      GoPeerService.uploadImage(image)
        .then((uploadResponse: any) => {
          links.push({
            title: "Screenshot",
            url: uploadResponse.response,
            text: "The current screen the student is looking at."
          });
        })
        .catch((error: any) => {
          links.push({
            title: "Screenshot",
            url: "",
            text: "Screenshot unavailable."
          });
        })
        .finally(() => {
          if (token) {
            const { gP } = window;
            gP.setEnv(getEnv());
            gP.identify(userData);
            gP.setToken(token);
            gP.setLinks(links);
            gP.launch();
            if (onClick) {
              onClick();
            }
          }
        });
    };

    return (
      <UxAction
        key={1}
        enabled={!disabled}
        text={text}
        priority={UxPriorityEnum.PRIMARY}
        callback={launchGoPeer}
        style={gradeButtonStyle()}
        className="px-3 ms-3"
      />
    );
  }

  return { GoPeerButton, addLink };
};

export default useGoPeer;
